/**
 * Shop owner calendar tab: day / week / month / year views, staff filter,
 * appointment details + client profile modal on slot press.
 */
import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaChartBar,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaMoneyBillAlt,
  FaClock,
  FaDollarSign,
  FaChevronDown,
} from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { http } from '../../lib/http';
import { cleanText } from './utils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = moment.months();
const HOURS_24 = Array.from({ length: 16 }, (_, i) => `${(i + 8).toString().padStart(2, '0')}:00`);

function ViewTabs({ mode, setMode }) {
  return (
    <nav className="view-tabs">
      {['day', 'week', 'month', 'year'].map((v) => (
        <button
          key={v}
          className={`v-btn ${mode === v ? 'active' : ''}`}
          onClick={() => setMode(v)}
        >
          {v.replace(/^\w/, (c) => c.toUpperCase())}
        </button>
      ))}
    </nav>
  );
}

function formatStatus(status) {
  const s = String(status || 'pending');
  return s.replace(/^\w/, (c) => c.toUpperCase());
}

export default function CalendarTab({ appts, biz }) {
  const { t, lang } = useI18n();
  const [mode, setMode] = useState('month');
  const [focus, setDate] = useState(moment());
  const [profileOpen, setProfileOpen] = useState(false);
  const [loadingProf, setLoadingProf] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [dayApptChoices, setDayApptChoices] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const scrollPositionRef = useRef(0);
  const scrollContainerRef = useRef(null);
  const clickedElementPositionRef = useRef(null);
  const modalOverlayRef = useRef(null);

  const closeModal = () => {
    clickedElementPositionRef.current = null;
    setProfileOpen(false);
    setSelectedAppt(null);
    setDayApptChoices(null);
    setClientInfo(null);
    setLoadingProf(false);
    setStatsOpen(false);
  };

  useLayoutEffect(() => {
    if (profileOpen) {
      const container = scrollContainerRef.current;
      const savedScroll = scrollPositionRef.current;
      if (!container) {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScroll}px`;
        document.body.style.width = '100%';
      }
      document.body.style.overflow = 'hidden';
      if (modalOverlayRef.current) {
        requestAnimationFrame(() => {
          modalOverlayRef.current.scrollTop = 0;
        });
      }
    } else {
      const savedScroll = scrollPositionRef.current;
      const container = scrollContainerRef.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      requestAnimationFrame(() => {
        if (container && typeof container.scrollTo === 'function') {
          container.scrollTo(0, savedScroll);
        } else {
          window.scrollTo(0, savedScroll);
        }
        scrollPositionRef.current = 0;
        scrollContainerRef.current = null;
        clickedElementPositionRef.current = null;
      });
    }
  }, [profileOpen]);

  const staffChoices = useMemo(
    () =>
      Array.isArray(biz?.staff) && biz.staff.length
        ? biz.staff
        : Array.isArray(biz?.staffMembers) && biz.staffMembers.length
          ? biz.staffMembers
          : [],
    [biz]
  );
  const [staffFilter, setStaffFilter] = useState('');

  const apptsForCal = useMemo(() => {
    if (!staffFilter) return appts;
    return appts.filter((a) => {
      const idMatch = a.staffId && String(a.staffId) === String(staffFilter);
      const staffObj = staffChoices.find((s) => String(s._id) === String(staffFilter));
      const nameMatch = staffObj?.name && a.staffName === staffObj.name;
      return idMatch || nameMatch;
    });
  }, [appts, staffFilter, staffChoices]);

  const booked = useMemo(() => {
    const o = {};
    for (const a of apptsForCal) o[`${a.selectedDate} ${a.selectedHour}`] = a;
    return o;
  }, [apptsForCal]);

  const go = (unit, val) => setDate((d) => d.clone().add(val, unit));
  const y = focus.year();
  const mIx = focus.month();
  const monthStart = focus.clone().startOf('month');
  const weekStart = focus.clone().startOf('week');
  const todayISO = moment().format('YYYY-MM-DD');

  const captureScroll = (event) => {
    const scrollEl = event?.currentTarget?.closest?.('.content-area') || document.querySelector('.content-area');
    scrollContainerRef.current = scrollEl || null;
    scrollPositionRef.current = scrollEl
      ? scrollEl.scrollTop
      : window.scrollY || document.documentElement.scrollTop || 0;

    const isLaptop = typeof window !== 'undefined' && window.innerWidth > 600;
    clickedElementPositionRef.current = null;
    if (isLaptop) {
      if (scrollEl && typeof scrollEl.scrollTo === 'function') scrollEl.scrollTo(0, 0);
      else window.scrollTo(0, 0);
    }
  };

  const openAppointmentDetails = async (booking, event) => {
    if (!booking) return;
    try {
      captureScroll(event);
      setDayApptChoices(null);
      setSelectedAppt(booking);
      setLoadingProf(true);
      setProfileOpen(true);
      setClientInfo(null);
      setStatsOpen(false);
      const userId = booking.user?._id || booking.user;
      if (!userId) {
        setClientInfo({ error: t('ownerCouldNotLoadClient') || 'Could not load client profile.' });
        return;
      }
      const data = await http.getJson(`/api/shopowner/client/${userId}/profile`);
      setClientInfo(data);
    } catch (e) {
      setClientInfo({ error: t('ownerCouldNotLoadClient') || 'Could not load client profile.' });
    } finally {
      setLoadingProf(false);
    }
  };

  const openMonthDay = (d, event) => {
    const iso = d.format('YYYY-MM-DD');
    const dayAppts = apptsForCal
      .filter((a) => a.selectedDate === iso)
      .sort((a, b) => String(a.selectedHour || '').localeCompare(String(b.selectedHour || '')));

    if (!dayAppts.length) {
      setDate(d);
      setMode('day');
      return;
    }
    if (dayAppts.length === 1) {
      openAppointmentDetails(dayAppts[0], event);
      return;
    }

    captureScroll(event);
    setSelectedAppt(null);
    setClientInfo(null);
    setLoadingProf(false);
    setDayApptChoices(dayAppts);
    setProfileOpen(true);
  };

  const StaffFilter = () =>
    staffChoices.length ? (
      <div className="staff-filter">
        <label htmlFor="cal-staff" className="visually-hidden">
          {t('labelStaff')}
        </label>
        <select id="cal-staff" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}>
          <option value="">{t('ownerAllStaff')}</option>
          {staffChoices.map((s) => (
            <option key={s._id || s.name} value={s._id || s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
    ) : null;

  const detailsModal =
    profileOpen &&
    ReactDOM.createPortal(
      <div
        ref={modalOverlayRef}
        className="client-modal-overlay"
        onClick={closeModal}
        style={{
          display: clickedElementPositionRef.current ? 'block' : 'flex',
          alignItems: clickedElementPositionRef.current ? 'center' : 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
          padding: clickedElementPositionRef.current ? '0' : '24px 0 0',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          boxShadow: 'none',
          filter: 'none',
          WebkitFilter: 'none',
        }}
      >
        <div
          className="client-modal modern-profile-modal cal-booking-modal"
          dir={lang === 'ar' ? 'rtl' : 'ltr'}
          role="dialog"
          aria-label={t('dashBookingDetails') || 'Appointment Details'}
          onClick={(e) => e.stopPropagation()}
          style={
            clickedElementPositionRef.current
              ? {
                  position: 'fixed',
                  top: `${clickedElementPositionRef.current.top}px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  maxHeight: 'calc(100vh - 40px)',
                  width: 'min(520px, 94vw)',
                  margin: '0',
                  zIndex: 1001,
                  boxShadow: 'none',
                  filter: 'none',
                  WebkitFilter: 'none',
                  textShadow: 'none',
                }
              : {
                  margin: 'auto',
                  maxHeight: 'calc(100vh - 40px)',
                  position: 'relative',
                  boxShadow: 'none',
                  filter: 'none',
                  WebkitFilter: 'none',
                  textShadow: 'none',
                }
          }
        >
          <div className="modern-profile-modal-header cal-booking-modal-header">
            <div className="modern-profile-modal-header-content">
              <div className="modern-profile-modal-title-container">
                <span className="cal-booking-modal-icon-wrap">
                  <FaCalendarAlt className="modern-profile-modal-icon" />
                </span>
                <h3 className="modern-profile-modal-title">
                  {dayApptChoices
                    ? t('ownerBookings') || 'Bookings'
                    : t('dashBookingDetails') || 'Appointment Details'}
                </h3>
              </div>
            </div>
            <button className="modern-profile-modal-close" onClick={closeModal} aria-label="Close">
              ×
            </button>
          </div>

          {dayApptChoices ? (
            <div className="modern-profile-modal-body cal-day-appt-choices">
              <p className="cal-day-appt-choices-hint">
                {t('ownerSelectAppointment') || 'Select an appointment'}
              </p>
              <ul className="cal-day-appt-list">
                {dayApptChoices.map((a) => (
                  <li key={a._id || `${a.selectedDate}-${a.selectedHour}`}>
                    <button
                      type="button"
                      className={`cal-day-appt-item status-${String(a.status || 'pending').toLowerCase()}`}
                      onClick={(e) => openAppointmentDetails(a, e)}
                    >
                      <span className="cal-day-appt-time">{cleanText(a.selectedHour || '—')}</span>
                      <span className="cal-day-appt-service">{a.serviceName || '—'}</span>
                      <span className="cal-day-appt-status">{formatStatus(a.status)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="modern-profile-modal-body cal-booking-modal-body">
              {selectedAppt && (
                <div className="modern-profile-section cal-booking-section">
                  <div className="cal-booking-hero">
                    <div className="cal-booking-hero-main">
                      <span className="cal-booking-hero-label">{t('ownerServiceNameLabel')}</span>
                      <h4 className="cal-booking-hero-service">{selectedAppt.serviceName || 'N/A'}</h4>
                      <div className="cal-booking-hero-id">
                        <span className="cal-booking-hero-id-label">{t('ownerAppointmentId')}</span>
                        <span className="cal-booking-hero-id-value">{selectedAppt._id || 'N/A'}</span>
                      </div>
                    </div>
                    <span
                      className={`cal-booking-status-pill status-${String(selectedAppt.status || 'pending').toLowerCase()}`}
                    >
                      {formatStatus(selectedAppt.status)}
                    </span>
                  </div>

                  <div className="cal-booking-grid">
                    <div className="cal-booking-tile">
                      <span className="cal-booking-tile-icon cal-booking-tile-icon--amber">
                        <FaCalendarAlt />
                      </span>
                      <div className="cal-booking-tile-text">
                        <span className="cal-booking-tile-label">{t('ownerDateLabel')}</span>
                        <span className="cal-booking-tile-value">
                          {selectedAppt.selectedDate
                            ? cleanText(moment(selectedAppt.selectedDate).format('D MMM YYYY'))
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="cal-booking-tile">
                      <span className="cal-booking-tile-icon cal-booking-tile-icon--amber">
                        <FaClock />
                      </span>
                      <div className="cal-booking-tile-text">
                        <span className="cal-booking-tile-label">{t('ownerTimeLabel')}</span>
                        <span className="cal-booking-tile-value">
                          {cleanText(selectedAppt.selectedHour || 'N/A')}
                        </span>
                      </div>
                    </div>
                    <div className="cal-booking-tile">
                      <span className="cal-booking-tile-icon">
                        <FaUser />
                      </span>
                      <div className="cal-booking-tile-text">
                        <span className="cal-booking-tile-label">{t('labelStaff')}</span>
                        <span className="cal-booking-tile-value">
                          {selectedAppt.staffName || selectedAppt.staff?.name || selectedAppt.staff || 'N/A'}
                        </span>
                      </div>
                    </div>
                    {selectedAppt.totalPrice != null && (
                      <div className="cal-booking-tile cal-booking-tile--price">
                        <span className="cal-booking-tile-icon cal-booking-tile-icon--amber">
                          <FaDollarSign />
                        </span>
                        <div className="cal-booking-tile-text">
                          <span className="cal-booking-tile-label">{t('labelPrice')}</span>
                          <span className="cal-booking-tile-value">
                            {cleanText(`${Number(selectedAppt.totalPrice).toLocaleString('en-US')} EGP`)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {loadingProf ? (
                <div className="client-modal-loading">
                  <div>{t('ownerLoadingClientProfile')}</div>
                </div>
              ) : clientInfo?.error ? (
                <div className="client-modal-error">
                  <div>{clientInfo.error}</div>
                </div>
              ) : clientInfo ? (
                <>
                  {clientInfo.profile && (
                    <div className="modern-profile-section cal-booking-client-section">
                      <div className="modern-profile-section-header">
                        <div className="modern-profile-section-header-left">
                          <div className="modern-profile-section-icon cal-booking-section-icon">
                            <FaUser />
                          </div>
                          <h4 className="modern-profile-section-title">{t('personalInformation')}</h4>
                        </div>
                      </div>

                      <div className="cal-booking-person-row">
                        <div className="cal-booking-person-name">
                          <span className="cal-booking-person-name-label">{t('name')}</span>
                          <span className="cal-booking-person-name-value">
                            {clientInfo.profile.name || 'N/A'}
                          </span>
                        </div>
                        <div className="cal-booking-person-actions">
                          {clientInfo.profile.phone && (
                            <a
                              href={`tel:${clientInfo.profile.phone}`}
                              className="cal-booking-icon-btn"
                              aria-label={t('callNow')}
                              title={clientInfo.profile.phone}
                            >
                              <FaPhone />
                            </a>
                          )}
                          {clientInfo.profile.email && (
                            <a
                              href={`mailto:${clientInfo.profile.email}`}
                              className="cal-booking-icon-btn"
                              aria-label={t('emailNow')}
                              title={clientInfo.profile.email}
                            >
                              <FaEnvelope />
                            </a>
                          )}
                        </div>
                      </div>

                      {clientInfo.stats && (
                        <div className="cal-booking-stats-dropdown">
                          <button
                            type="button"
                            className={`cal-booking-stats-toggle ${statsOpen ? 'is-open' : ''}`}
                            onClick={() => setStatsOpen((v) => !v)}
                            aria-expanded={statsOpen}
                          >
                            <span className="cal-booking-stats-toggle-left">
                              <FaChartBar className="cal-booking-stats-toggle-icon" aria-hidden />
                              {t('ownerBookingStats')}
                            </span>
                            <FaChevronDown className="cal-booking-stats-chevron" aria-hidden />
                          </button>

                          {statsOpen && (
                            <div className="cal-booking-stats-panel">
                              <div className="modern-stats-grid">
                                <div className="modern-stat-card">
                                  <div className="modern-stat-icon">
                                    <FaCalendarAlt />
                                  </div>
                                  <div className="modern-stat-value">
                                    {cleanText(
                                      clientInfo.stats?.totalBookings || clientInfo.stats?.total || 0
                                    )}
                                  </div>
                                  <div className="modern-stat-label">{t('ownerTotalBookings')}</div>
                                </div>
                                <div className="modern-stat-card">
                                  <div className="modern-stat-icon">
                                    <FaCheckCircle />
                                  </div>
                                  <div className="modern-stat-value">
                                    {cleanText(clientInfo.stats.completed || 0)}
                                  </div>
                                  <div className="modern-stat-label">{t('dashStatCompleted')}</div>
                                </div>
                                <div className="modern-stat-card">
                                  <div className="modern-stat-icon">
                                    <FaTimesCircle />
                                  </div>
                                  <div className="modern-stat-value">
                                    {cleanText(clientInfo.stats.cancelled || 0)}
                                  </div>
                                  <div className="modern-stat-label">{t('ownerCancelled')}</div>
                                </div>
                                {clientInfo.stats?.spendEGP && (
                                  <div className="modern-stat-card">
                                    <div className="modern-stat-icon">
                                      <FaMoneyBillAlt />
                                    </div>
                                    <div className="modern-stat-value">
                                      {cleanText(`${clientInfo.stats.spendEGP} EGP`)}
                                    </div>
                                    <div className="modern-stat-label">{t('ownerTotalSpentEGP')}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>,
      document.body
    );

  if (mode === 'year') {
    return (
      <>
        <div className="cal shell calendar-tab-spacing">
          <header className="cal bar">
            <button onClick={() => go('year', -1)} className="nav">
              ‹
            </button>
            <h2>{cleanText(y)}</h2>
            <button onClick={() => go('year', 1)} className="nav">
              ›
            </button>
            <ViewTabs mode={mode} setMode={setMode} />
            <StaffFilter />
          </header>
          <div className="grid year">
            {MONTHS.map((nm, i) => (
              <button
                key={nm}
                className="card month"
                onClick={() => {
                  setMode('month');
                  setDate(focus.clone().month(i));
                }}
              >
                {nm}
              </button>
            ))}
          </div>
        </div>
        {detailsModal}
      </>
    );
  }

  if (mode === 'month') {
    const start = monthStart.clone().startOf('week');
    const cells = Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'd'));

    return (
      <>
        <div className="cal shell calendar-tab-spacing">
          <header className="cal bar">
            <button onClick={() => go('month', -1)} className="nav">
              ‹
            </button>
            <h2 onClick={() => setMode('year')}>
              {cleanText(`${MONTHS[mIx]} ${y}`)}
            </h2>
            <button onClick={() => go('month', 1)} className="nav">
              ›
            </button>
            <ViewTabs mode={mode} setMode={setMode} />
            <StaffFilter />
          </header>

          <div className="wkdays">
            {WEEKDAYS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="grid month">
            {cells.map((d) => {
              const iso = d.format('YYYY-MM-DD');
              const otherMon = d.month() !== mIx;
              const busy = HOURS_24.some((h) => booked[`${iso} ${h}`]);
              const today = iso === todayISO;
              return (
                <button
                  key={iso}
                  type="button"
                  className={['cell', otherMon && 'other', busy && 'busy', today && 'today']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={(e) => openMonthDay(d, e)}
                  title={busy ? t('dashBookingDetails') || 'View bookings' : ''}
                >
                  <span className="num">{cleanText(d.date())}</span>
                  {busy && <span className="dot" />}
                </button>
              );
            })}
          </div>
        </div>
        {detailsModal}
      </>
    );
  }

  const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'd'));

  if (mode === 'week') {
    return (
      <>
        <div className="cal shell wide calendar-tab-spacing">
          <header className="cal bar">
            <button onClick={() => go('week', -1)} className="nav">
              ‹
            </button>
            <h2 onClick={() => setMode('month')}>
              {weekStart.format('D MMM')} – {weekStart.clone().endOf('week').format('D MMM YYYY')}
            </h2>
            <button onClick={() => go('week', 1)} className="nav">
              ›
            </button>
            <ViewTabs mode={mode} setMode={setMode} />
            <StaffFilter />
          </header>

          <div className="grid week">
            <div className="time col-hdr" />
            {weekDays.map((d) => (
              <div key={d} className="col-hdr">
                <span className="wd">{d.format('ddd')}</span>
              </div>
            ))}

            {HOURS_24.map((h) => (
              <React.Fragment key={h}>
                <div className="time">{cleanText(h.replace(':00', ''))}</div>
                {weekDays.map((d) => {
                  const iso = d.format('YYYY-MM-DD');
                  const apt = booked[`${iso} ${h}`];
                  return (
                    <div
                      key={iso + h}
                      role={apt ? 'button' : undefined}
                      tabIndex={apt ? 0 : undefined}
                      className={`slot ${apt ? apt.status : ''} ${apt ? 'slot--clickable' : ''}`}
                      title={apt ? `${apt.serviceName} (${apt.status})` : ''}
                      onClick={apt ? (e) => openAppointmentDetails(apt, e) : undefined}
                      onKeyDown={
                        apt
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openAppointmentDetails(apt, e);
                              }
                            }
                          : undefined
                      }
                    >
                      {apt && <span>{formatStatus(apt.status)}</span>}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
        {detailsModal}
      </>
    );
  }

  const dayISO = focus.format('YYYY-MM-DD');
  return (
    <>
      <div className="cal shell calendar-tab-spacing">
        <header className="cal bar">
          <button onClick={() => go('day', -1)} className="nav">
            ‹
          </button>
          <h2 onClick={() => setMode('month')}>
            {cleanText(focus.format('D MMMM YYYY'))}
          </h2>
          <button onClick={() => go('day', 1)} className="nav">
            ›
          </button>
          <ViewTabs mode={mode} setMode={setMode} />
          <StaffFilter />
        </header>

        <div className="day col">
          {HOURS_24.map((h) => {
            const apt = booked[`${dayISO} ${h}`];
            const status = String(apt?.status || '').toLowerCase();
            return (
              <div
                key={h}
                role={apt ? 'button' : undefined}
                tabIndex={apt ? 0 : undefined}
                className={`slot ${apt ? apt.status : ''} ${apt ? 'slot--clickable' : ''}`}
                onClick={apt ? (e) => openAppointmentDetails(apt, e) : undefined}
                onKeyDown={
                  apt
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openAppointmentDetails(apt, e);
                        }
                      }
                    : undefined
                }
              >
                <time>{cleanText(h.replace(':00', ''))}</time>
                {apt ? (
                  <>
                    <span className={`st st--slot-place ${status}`}>{apt.status}</span>
                    <div className="slot-info slot-info--with-action">
                      <span className="serv">{apt.serviceName}</span>
                      <button
                        type="button"
                        className="view-profile-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAppointmentDetails(apt, e);
                        }}
                      >
                        {t('ownerViewClientProfile')}
                      </button>
                    </div>
                  </>
                ) : (
                  <span className="avail">{t('ownerAvailable')}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {detailsModal}
    </>
  );
}
